/**
 * API Configuration for TalkFlow Widget
 * 
 * Centralized configuration for AI server endpoints
 */

// Import environment override
import './environment.js';

// Environment configuration with fallback
const CONFIG = {
    // Production AI Server (use relative URLs for Nginx proxy)
    PRODUCTION: {
        AI_SERVER_URL: '',  // Empty string means relative URLs for production
        API_VERSION: 'v1'
    },

    // Local Development AI Server  
    DEVELOPMENT: {
        AI_SERVER_URL: 'http://localhost:3000',
        API_VERSION: 'v1'
    },
};

// Try to override with environment variables if available
if (typeof process !== 'undefined' && process.env) {
    if (process.env.REACT_APP_AI_SERVER_URL) {
        CONFIG.PRODUCTION.AI_SERVER_URL = process.env.REACT_APP_AI_SERVER_URL;
        CONFIG.DEVELOPMENT.AI_SERVER_URL = process.env.REACT_APP_AI_SERVER_URL;
    }
    if (process.env.REACT_APP_API_VERSION) {
        CONFIG.PRODUCTION.API_VERSION = process.env.REACT_APP_API_VERSION;
        CONFIG.DEVELOPMENT.API_VERSION = process.env.REACT_APP_API_VERSION;
    }
}

// Auto-detect environment based on hostname
// Options: 'PRODUCTION', 'DEVELOPMENT'
const detectEnvironment = () => {
    // Check if running on production domain
    if (typeof window !== 'undefined' && window.location) {
        const hostname = window.location.hostname;
        if (hostname === 'tailm.id.vn' || hostname === 'www.tailm.id.vn') {
            return 'PRODUCTION';
        }
    }

    // Check for production environment variable
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production') {
        return 'PRODUCTION';
    }

    // Default to development
    return 'DEVELOPMENT';
};

const CURRENT_ENV = detectEnvironment();

// Get current configuration
const getCurrentConfig = () => {
    return CONFIG[CURRENT_ENV];
};

// Get API base URL
export const getApiBaseUrl = () => {
    const config = getCurrentConfig();
    const baseUrl = config.AI_SERVER_URL;

    // Return relative URL for production (empty baseUrl)
    if (!baseUrl || baseUrl === '') {
        return '';  // This will result in relative URLs
    }

    return baseUrl;
};

// Get API URL with version
export const getApiUrl = (endpoint = '') => {
    const config = getCurrentConfig();
    const baseUrl = config.AI_SERVER_URL;
    const version = config.API_VERSION;

    // Remove leading slash from endpoint if present
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;

    // Handle relative URLs for production (Nginx proxy)
    if (!baseUrl || baseUrl === '') {
        // Use relative URLs - will be proxied by Nginx
        if (cleanEndpoint.startsWith('api/')) {
            return `/${cleanEndpoint}`;
        } else if (cleanEndpoint) {
            return `/api/${version}/${cleanEndpoint}`;
        } else {
            return `/api/${version}`;
        }
    }

    // Build URL with base URL for development
    if (cleanEndpoint.startsWith('api/')) {
        // Endpoint already includes 'api/', just add base URL
        return `${baseUrl}/${cleanEndpoint}`;
    } else if (cleanEndpoint) {
        // Add version prefix
        return `${baseUrl}/api/${version}/${cleanEndpoint}`;
    } else {
        // Base API URL
        return `${baseUrl}/api/${version}`;
    }
};

// Environment info
export const getEnvironmentInfo = () => {
    return {
        current: CURRENT_ENV,
        config: getCurrentConfig(),
        available: Object.keys(CONFIG)
    };
};

// Health check URL
export const getHealthUrl = () => {
    const config = getCurrentConfig();
    return `${config.AI_SERVER_URL}/api/${config.API_VERSION}/health`;
};

// Legacy support - default API URL for backward compatibility
export const DEFAULT_API_URL = getApiBaseUrl();

export default {
    getApiBaseUrl,
    getApiUrl,
    getEnvironmentInfo,
    getHealthUrl,
    DEFAULT_API_URL,
    CURRENT_ENV
}; 